/**
 * Session Join Page
 *
 * Phase 1 task 6: public viewer entry point. Lands on /join/{sessionId},
 * fetches the session by id, then composes the existing transcript viewer
 * alongside the Phase 2 LiveKit video pane.
 *
 * Phase 2 task 7+8 additions (this file):
 *   - When the SSO/livekit feature flag is on AND the session announces
 *     `hasVideo || hasScreencast`, fetch a viewer token and render
 *     `<LiveKitRoom>` containing `<RoomAudioRenderer />` + a `useTracks`-driven
 *     video pane. Otherwise the page degrades to the original transcript-only
 *     layout — no regression for sessions without video.
 *   - Layout toggle (split / text-only / video-only). Default split. Stacks
 *     vertically below the `lg` breakpoint to match the existing responsive
 *     posture.
 *   - "Start presenting" button visible only to the session creator (or any
 *     authenticated user — the auth gate on `/sessions/:id/present` is the
 *     real authorisation check; this just nudges them toward the right page).
 *
 * Reuses `TranscriptPanel` (which itself wraps `useTranscriptSocket`) keyed
 * by the session's `documentId` — the transcript flow has not changed.
 */

import { LiveKitRoom, RoomAudioRenderer, useTracks } from '@livekit/components-react'
import { Track } from 'livekit-client'
import {
	ArrowLeft,
	Check,
	Copy,
	FileText,
	Layout,
	Loader2,
	Monitor,
	Play,
	QrCode,
	Video,
	VideoOff,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { Alert, Button, buttonClasses, cn, EmptyState, IconButton, Modal } from '../components/ui'
import { EnableAudioButton } from '../components/viewer/enable-audio-button'
import { IosVideoFix } from '../components/viewer/ios-video-fix'
import { PresenterScreenTrack } from '../components/viewer/presenter-screen-track'
import { TranscriptPanel } from '../components/viewer/transcript-panel'
import { useLivekitToken } from '../hooks/use-livekit-token'
import { usePageTitle } from '../hooks/use-page-title'
import { useSsoConfig } from '../hooks/use-sso-config'
import { useSession } from '../lib/auth-client'
import { relativeTime } from '../lib/format-time'
import { trpc } from '../trpc/client'

type LayoutMode = 'split' | 'text' | 'video'

const LAYOUT_KEY = 'polygraf-join-layout'

function loadLayout(): LayoutMode {
	try {
		const saved = localStorage.getItem(LAYOUT_KEY)
		if (saved === 'split' || saved === 'text' || saved === 'video') return saved
	} catch {
		// localStorage unavailable (e.g. private mode) — fall back to default.
	}
	return 'split'
}

function saveLayout(mode: LayoutMode) {
	try {
		localStorage.setItem(LAYOUT_KEY, mode)
	} catch {
		// best effort
	}
}

export default function SessionJoinPage() {
	const { t, i18n } = useTranslation()
	const { sessionId } = useParams<{ sessionId: string }>()
	const { data: authSession } = useSession()
	const { livekitEnabled } = useSsoConfig()
	const [shareOpen, setShareOpen] = useState(false)
	const [shareCopied, setShareCopied] = useState(false)
	const [shareCopyFailed, setShareCopyFailed] = useState(false)

	const sessionQuery = trpc.sessions.get.useQuery(
		{ id: sessionId ?? '' },
		{
			enabled: !!sessionId,
			// Refresh occasionally so the page picks up `status -> ENDED`
			// transitions without a full reload (Phase 2 will use the WS feed
			// for this — keep polling minimal until then).
			refetchInterval: 30_000,
			refetchOnWindowFocus: true,
		},
	)

	const session = sessionQuery.data
	usePageTitle(session?.title ?? t('sessions.list.title'))
	const hasVideoSource = !!(session?.hasVideo || session?.hasScreencast)
	// A room-linked broadcast without a transcript redirects to /view/screen
	// below — don't spend a viewer token on a page that is about to leave.
	const redirectsToServiceViewer = !!session?.serviceId && !session?.hasTranscript
	const tokenEnabled =
		!!sessionId &&
		livekitEnabled &&
		hasVideoSource &&
		session?.status === 'ACTIVE' &&
		!redirectsToServiceViewer

	// r65: `/join/:sessionId` can now carry a 6-char short code instead of the
	// session's cuid. Downstream calls (the LiveKit token route looks sessions
	// up by primary key) must use the RESOLVED id from the fetched row, never
	// the raw URL param. `tokenEnabled` already requires the session to be
	// loaded (status check), so `session?.id` is set whenever the hook runs.
	const tokenResult = useLivekitToken(session?.id, { enabled: tokenEnabled })

	if (!sessionId) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-base-200 p-4">
				<EmptyState
					icon={<Monitor size={32} />}
					title={t('sessions.join.errors.missingId')}
					description={t('sessions.join.errors.missingIdDesc')}
					action={
						<Link to="/sessions" className={buttonClasses()}>
							{t('sessions.join.backToList')}
						</Link>
					}
				/>
			</div>
		)
	}

	if (sessionQuery.isLoading) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen bg-base-200 gap-3">
				<Loader2 size={32} className="animate-spin text-primary" />
				<p className="text-sm text-base-content/60">{t('sessions.join.loading')}</p>
			</div>
		)
	}

	if (sessionQuery.isError || !session) {
		const message = sessionQuery.error?.message ?? t('sessions.join.errors.notFound')
		return (
			<div className="flex items-center justify-center min-h-screen bg-base-200 p-4">
				<EmptyState
					icon={<VideoOff size={32} />}
					title={t('sessions.join.errors.notFoundTitle')}
					description={message}
					action={
						<Link to="/sessions" className={buttonClasses()}>
							{t('sessions.join.backToList')}
						</Link>
					}
				/>
			</div>
		)
	}

	// A broadcast linked to a room's screen service *is* that service. Its
	// viewer (/view/screen/:id) owns the viewer controls (settings panel,
	// immersive mode, unmute) and is the one public address the room page
	// shows (MU review, 24. 8. 2026). /join/:code stays for one-off sessions,
	// for broadcasts that also carry a transcript (the screen viewer has no
	// transcript pane) and for ended sessions (the screen viewer would report
	// the service as unconfigured instead of "this broadcast is over").
	const isEnded = session.status === 'ENDED'
	if (session.serviceId && !session.hasTranscript && !isEnded) {
		return <Navigate to={`/view/screen/${session.serviceId}`} replace />
	}

	const isCreator = !!authSession?.user && authSession.user.id === session.createdBy.id
	const showPresentButton = livekitEnabled && isCreator && !isEnded
	// r65: share the dictatable short URL when the session has a code; the
	// cuid URL stays the fallback for pre-r65 sessions.
	const shareUrl = `${window.location.origin}/join/${session.shortCode ?? session.id}`

	// Decide whether to enter LiveKit at all. We avoid mounting <LiveKitRoom>
	// when the token failed (e.g. server returned 503 because LiveKit is not
	// configured for this deployment), keeping a clean transcript-only UX.
	const showVideoArea = tokenEnabled && tokenResult.status !== 'error'
	const showLiveKit =
		showVideoArea && tokenResult.status === 'ready' && !!tokenResult.token && !!tokenResult.url

	return (
		<div className="flex flex-col h-screen bg-base-200">
			{/* Header */}
			<header className="bg-base-100 border-b border-base-300 px-4 sm:px-6 py-3 shrink-0">
				<div className="max-w-5xl mx-auto flex items-center gap-3 flex-wrap">
					<Link
						to="/sessions"
						className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-xl')}
						aria-label={t('common.back')}
					>
						<ArrowLeft size={20} />
					</Link>
					<div className="flex-1 min-w-0">
						<h1 className="text-lg font-display font-semibold truncate">{session.title}</h1>
						<p className="text-xs text-base-content/60 truncate">
							{t('sessions.join.byCreator', {
								name: session.createdBy.name || t('sessions.join.unknownCreator'),
							})}
						</p>
						{/* MP-165: surface the session's created time on the public join
						    page too — it was only shown on the sessions list + admin. */}
						<p className="text-xs text-base-content/50 truncate">
							{relativeTime(session.startedAt, i18n.language)}
						</p>
					</div>
					{!isEnded && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setShareOpen(true)}
							aria-label={t('sessions.join.share.button')}
							leadingIcon={<QrCode size={16} />}
						>
							<span className="hidden sm:inline">{t('sessions.join.share.button')}</span>
						</Button>
					)}
					{showPresentButton && (
						<Link
							to={`/sessions/${session.id}/present`}
							className={cn(buttonClasses(), 'inline-flex items-center gap-1.5 text-sm')}
						>
							<Play size={14} />
							{t('sessions.join.startPresenting')}
						</Link>
					)}
					{isEnded ? (
						<span className="inline-flex items-center gap-1 text-xs bg-base-300 text-base-content/70 px-2 py-1 rounded-full">
							{t('sessions.join.status.ended')}
						</span>
					) : (
						<span className="inline-flex items-center gap-1 text-xs bg-success/10 text-success px-2 py-1 rounded-full">
							<span
								className="inline-flex w-1.5 h-1.5 rounded-full bg-success animate-pulse"
								aria-hidden="true"
							/>
							{t('sessions.join.status.live')}
						</span>
					)}
				</div>
			</header>

			{/* Content */}
			<div className="flex-1 min-h-0 overflow-hidden">
				{isEnded ? (
					<div className="flex items-center justify-center h-full p-4">
						<EmptyState
							icon={<VideoOff size={32} />}
							title={t('sessions.join.endedTitle')}
							description={t('sessions.join.endedDesc')}
							action={
								<Link to="/sessions" className={buttonClasses()}>
									{t('sessions.join.backToList')}
								</Link>
							}
						/>
					</div>
				) : (
					<JoinViewerLayout
						session={session}
						showVideoArea={showVideoArea}
						showLiveKit={showLiveKit}
						tokenLoading={tokenResult.status === 'loading'}
						tokenUrl={tokenResult.url}
						tokenValue={tokenResult.token}
					/>
				)}
			</div>

			<ShareModal
				open={shareOpen}
				url={shareUrl}
				title={session.title}
				copied={shareCopied}
				copyFailed={shareCopyFailed}
				onCopy={async () => {
					try {
						await navigator.clipboard.writeText(shareUrl)
						setShareCopyFailed(false)
						setShareCopied(true)
						setTimeout(() => setShareCopied(false), 2000)
					} catch {
						// Clipboard API may be unavailable (insecure context, denied
						// permission) — surface a manual-copy hint instead of failing
						// silently.
						setShareCopyFailed(true)
					}
				}}
				onClose={() => {
					setShareCopyFailed(false)
					setShareOpen(false)
				}}
			/>
		</div>
	)
}

function ShareModal({
	open,
	url,
	title,
	copied,
	copyFailed,
	onCopy,
	onClose,
}: {
	open: boolean
	url: string
	title: string
	copied: boolean
	copyFailed: boolean
	onCopy: () => void
	onClose: () => void
}) {
	const { t } = useTranslation()
	return (
		<Modal
			open={open}
			onClose={onClose}
			title={t('sessions.join.share.title')}
			description={title}
			size="md"
			closeLabel={t('common.close')}
		>
			<div className="space-y-5">
				<div className="flex justify-center py-2">
					<div className="bg-white p-4 rounded-xl shadow-soft">
						<QRCodeSVG
							value={url}
							size={220}
							level="M"
							title={t('sessions.join.share.qrAlt', { title })}
						/>
					</div>
				</div>

				<div className="space-y-2">
					<label htmlFor="share-url" className="text-sm font-medium block">
						{t('sessions.create.success.joinUrl')}
					</label>
					<div className="flex items-center gap-2">
						<code id="share-url" className="text-sm bg-base-200 px-3 py-2 rounded flex-1 truncate">
							{url}
						</code>
						<IconButton
							variant="ghost"
							size="sm"
							onClick={onCopy}
							aria-label={copied ? t('common.copied') : t('common.copy')}
						>
							{copied ? <Check className="text-success" /> : <Copy />}
						</IconButton>
					</div>
					{copyFailed && <Alert tone="error">{t('sessions.join.share.copyManual')}</Alert>}
				</div>

				<p className="text-sm text-base-content/60 text-center">
					{t('sessions.create.success.qrHint')}
				</p>
			</div>
		</Modal>
	)
}

interface JoinViewerLayoutProps {
	session: {
		documentId: string
		hasTranscript: boolean
		hasVideo: boolean
		hasScreencast: boolean
	}
	showVideoArea: boolean
	showLiveKit: boolean
	tokenLoading: boolean
	tokenUrl: string | null
	tokenValue: string | null
}

function JoinViewerLayout({
	session,
	showVideoArea,
	showLiveKit,
	tokenLoading,
	tokenUrl,
	tokenValue,
}: JoinViewerLayoutProps) {
	const { t } = useTranslation()
	// Layout state — only meaningful when we actually have a video pane to
	// toggle to. When `!showVideoArea` we always render text-only.
	const [layout, setLayout] = useStoredLayout()
	// Surface LiveKit connection failures instead of swallowing them in the
	// console — viewers (incl. screen-reader users) need to know the stream
	// dropped and that a refresh may recover it.
	const [livekitError, setLivekitError] = useState<string | null>(null)

	// When the session has only one medium, never force the split: a
	// video-only session would otherwise waste half the viewport on the
	// "no transcript yet" placeholder (MP-195). Collapse to the medium present.
	const effectiveLayout: LayoutMode = !showVideoArea
		? 'text'
		: !session.hasTranscript
			? 'video'
			: layout

	const showTranscript = effectiveLayout !== 'video'
	const showVideoPane = effectiveLayout !== 'text' && showVideoArea

	return (
		<div className="h-full flex flex-col min-h-0">
			{showVideoArea && session.hasTranscript && (
				<LayoutToggle current={effectiveLayout} onChange={setLayout} />
			)}
			<div className="flex-1 min-h-0 flex flex-col lg:flex-row">
				{showTranscript && (
					<div
						className={`flex-1 min-h-0 lg:basis-0 flex flex-col bg-base-100 ${
							effectiveLayout === 'text' ? '' : 'border-b lg:border-b-0 lg:border-r border-base-300'
						}`}
						data-testid="session-transcript-pane"
					>
						{session.hasTranscript ? (
							<TranscriptPanel sessionId={session.documentId} className="flex-1 min-h-0" />
						) : (
							<div className="flex-1 flex items-center justify-center p-6">
								<Alert tone="info">{t('sessions.join.noTranscript')}</Alert>
							</div>
						)}
					</div>
				)}
				{showVideoPane && (
					<div
						className="flex-1 min-h-0 lg:basis-0 flex flex-col bg-base-200"
						data-testid="session-video-pane"
					>
						{showLiveKit && tokenValue && tokenUrl ? (
							<LiveKitRoom
								token={tokenValue}
								serverUrl={tokenUrl}
								connect={true}
								video={false}
								audio={false}
								className="flex-1 min-h-0 flex flex-col"
								onConnected={() => setLivekitError(null)}
								onError={(err) => {
									console.error('[LiveKit] viewer room error', err)
									setLivekitError(t('sessions.join.video.connectionError'))
								}}
							>
								{livekitError && (
									<div className="p-3">
										<Alert tone="error">{livekitError}</Alert>
									</div>
								)}
								<RoomAudioRenderer />
								<EnableAudioButton className="self-center my-2" />
								<ViewerVideoPane />
							</LiveKitRoom>
						) : (
							<div className="flex-1 flex items-center justify-center p-6">
								{tokenLoading ? (
									<div className="flex flex-col items-center gap-2 text-base-content/60">
										<Loader2 size={28} className="animate-spin" />
										<p className="text-sm">{t('sessions.join.video.connecting')}</p>
									</div>
								) : (
									<EmptyState
										icon={<Video size={32} />}
										title={t('sessions.join.video.waitingTitle')}
										description={t('sessions.join.video.waitingDesc')}
									/>
								)}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

function ViewerVideoPane() {
	const { t } = useTranslation()
	// MP-163: the grid container is observed by IosVideoFix so every LiveKit
	// <video> it renders gets muted + playsInline. Without those, the browser's
	// autoplay policy blocks playback and the video never starts after connect
	// (and iOS Safari refuses inline play) — the viewer sat on "connected" with
	// no picture. VideoPanel already does this; ViewerVideoPane didn't.
	const videoGridRef = useRef<HTMLDivElement | null>(null)
	const tracks = useTracks([Track.Source.ScreenShare, Track.Source.Camera], {
		onlySubscribed: true,
	})

	// Filter out our own (local) tracks just in case the same identity is in
	// the room twice — the viewer should only ever render remote presenter
	// publishes.
	const remoteTracks = useMemo(() => tracks.filter((t) => !t.participant.isLocal), [tracks])

	if (remoteTracks.length === 0) {
		return (
			<div className="flex-1 flex items-center justify-center p-6 text-base-content/60">
				<div className="flex flex-col items-center gap-2">
					<Video size={28} className="opacity-60" />
					<p className="text-sm">{t('sessions.join.video.noPublishers')}</p>
				</div>
			</div>
		)
	}

	// Single presenter (the common case): render full-bleed/centered via the
	// shared renderer so it frames identically to the room viewer — no bespoke
	// black-card chrome, no per-track label (there's only one). The grid +
	// labeled cards stay ONLY as the multi-presenter fallback so viewers can
	// still tell tiles apart. `videoGridRef` wraps both so IosVideoFix keeps
	// muting/inlining every <video> it renders (autoplay policy, MP-163).
	const single = remoteTracks.length === 1 ? remoteTracks[0] : null

	if (single) {
		return (
			<div ref={videoGridRef} className="flex-1 min-h-0">
				<IosVideoFix containerRef={videoGridRef} />
				<PresenterScreenTrack trackRef={single} />
			</div>
		)
	}

	return (
		<div
			ref={videoGridRef}
			className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-2 p-2 overflow-auto"
		>
			<IosVideoFix containerRef={videoGridRef} />
			{remoteTracks.map((trackRef) => (
				<PresenterScreenTrack
					key={`${trackRef.participant.identity}-${trackRef.source}-${trackRef.publication?.trackSid ?? ''}`}
					trackRef={trackRef}
					showLabel
				/>
			))}
		</div>
	)
}

function LayoutToggle({
	current,
	onChange,
}: {
	current: LayoutMode
	onChange: (next: LayoutMode) => void
}) {
	const { t } = useTranslation()
	const options: Array<{ id: LayoutMode; label: string; icon: typeof Layout }> = [
		{ id: 'split', label: t('sessions.join.layout.split'), icon: Layout },
		{ id: 'text', label: t('sessions.join.layout.text'), icon: FileText },
		{ id: 'video', label: t('sessions.join.layout.video'), icon: Video },
	]
	return (
		<div className="px-4 py-2 bg-base-100 border-b border-base-300 shrink-0 flex items-center gap-2 justify-end">
			<span className="text-xs text-base-content/60 mr-1">{t('sessions.join.layout.label')}</span>
			<div
				role="group"
				aria-label={t('sessions.join.layout.label')}
				className="inline-flex bg-base-200 rounded-lg p-1 gap-1"
			>
				{options.map(({ id, label, icon: Icon }) => (
					<button
						key={id}
						type="button"
						onClick={() => onChange(id)}
						aria-pressed={current === id}
						className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors ${
							current === id
								? 'bg-base-100 shadow-sm font-medium'
								: 'text-base-content/60 hover:text-base-content'
						}`}
					>
						<Icon size={12} />
						{label}
					</button>
				))}
			</div>
		</div>
	)
}

function useStoredLayout(): [LayoutMode, (next: LayoutMode) => void] {
	// Lazy-initialise from localStorage so the first render doesn't flash the
	// default "split" layout before re-rendering with the saved choice.
	const [value, setValue] = useState<LayoutMode>(() =>
		typeof window === 'undefined' ? 'split' : loadLayout(),
	)
	const set = useCallback((next: LayoutMode) => {
		setValue(next)
		saveLayout(next)
	}, [])
	return [value, set]
}
